import { useMutation, UseMutationResult, useQuery, UseQueryResult } from '@tanstack/react-query';
import { fetchAuthenticatedForJson } from '../utils';
import { Afzender, Relatie } from '../types/relatie';
import { Pand, Verblijfsobject } from '../types/bag';
import { fetchBagPanden, fetchBagVerblijfsobjecten } from '../utils/bagApi';
import { LatLngBounds } from 'leaflet';
import { getRegistrationYear, hasOwnershipStatus, OwnershipType } from '../types/ownership';

const QUERY_KEY = ['input'];
const BASE_URL = import.meta.env.VITE_INPUT_API_URL;
const BASE_URL_v2 = `${import.meta.env.VITE_INPUT_API_URL}/v2`;
const BASE_URL_V3 = `${import.meta.env.VITE_INPUT_API_URL}/v3`;
const BAG_WFS_URL = import.meta.env.VITE_INPUT_API_BAG_URL;

const getManualInputBaseUrl = (registratiejaar: number): string => {
  if (registratiejaar > 2025) {
    return BASE_URL_V3;
  }

  return registratiejaar == 2025 ? BASE_URL_v2 : BASE_URL;
};

const normalizeEigendomstypeDeelnemer = (relatie: Relatie): Relatie => {
  const registratiejaar = getRegistrationYear(relatie.registratiejaar);
  const { eigendomstypeDeelnemer, ...relatieWithoutOwnership } = relatie;

  if (!hasOwnershipStatus(registratiejaar)) {
    return relatieWithoutOwnership;
  }

  return {
    ...relatieWithoutOwnership,
    eigendomstypeDeelnemer:
      eigendomstypeDeelnemer !== null &&
      eigendomstypeDeelnemer !== undefined &&
      eigendomstypeDeelnemer >= OwnershipType.Unknown &&
      eigendomstypeDeelnemer <= OwnershipType.NotReviewed
        ? eigendomstypeDeelnemer
        : null,
  };
};

const normalizeV2EigendomstypeDeelnemer = (manualInput: Afzender): Afzender => ({
  ...manualInput,
  relatie: manualInput.relatie.map(relatie => {
    const normalizedRelatie = normalizeEigendomstypeDeelnemer(relatie);
    const { eigendomstypeDeelnemer, ...relatieWithoutOwnership } = normalizedRelatie;

    return {
      ...relatieWithoutOwnership,
      EigendomstypeDeelnemer: eigendomstypeDeelnemer,
    } as Relatie;
  }),
});

export const useGetHandmatigeInvoer = (year?: string, uonummer?: string): UseQueryResult<Relatie> => {
  const registratiejaar = getRegistrationYear(year);
  const params = `input?year=${year}&uonummer=${uonummer}`;
  const endpoint = `${getManualInputBaseUrl(registratiejaar)}/${params}`;
  return useQuery({
    queryKey: [...QUERY_KEY, 'rapportage', year, uonummer],
    queryFn: () => fetchAuthenticatedForJson<Relatie>(endpoint).then(normalizeEigendomstypeDeelnemer),
    enabled: year != undefined && uonummer != undefined,
    retry: false,
    staleTime: 0,
  });
};

export const useGetDeskundige = (): UseQueryResult<{ label: string; value: string }[]> =>
  useQuery({
    queryKey: [...QUERY_KEY, 'deskundige'],
    queryFn: () => fetchAuthenticatedForJson<{ label: string; value: string }[]>(`${BASE_URL}/input/deskundige`),
  });
export const useGetSectoren = (): UseQueryResult<{ code: string; omschrijving: string }[]> =>
  useQuery({
    queryKey: [...QUERY_KEY, 'sectoren'],
    queryFn: () => fetchAuthenticatedForJson<{ code: string; omschrijving: string }[]>(`${BASE_URL}/input/sectoren`),
  });

export const useGetGewassen = (): UseQueryResult<{ code: string; omschrijving: string; sector: { code: string; omschrijving: string } }[]> =>
  useQuery({
    queryKey: [...QUERY_KEY, 'gewassen'],
    queryFn: () =>
      fetchAuthenticatedForJson<{ code: string; omschrijving: string; sector: { code: string; omschrijving: string } }[]>(`${BASE_URL}/input/gewassen`),
  });

export const useGetPanden = (bounds: LatLngBounds | null, enabled: boolean): UseQueryResult<Pand[]> => {
  return useQuery({
    queryKey: ['panden', bounds],
    queryFn: () => (bounds && enabled ? fetchBagPanden(`${BAG_WFS_URL}&TYPENAME=bag:pand`, bounds) : Promise.resolve([])),
    staleTime: 60 * 1000,
    enabled: !!bounds && enabled,
    placeholderData: previousData => previousData,
  });
};

export const useGetVerblijfsobjecten = (bounds: LatLngBounds | null, enabled: boolean): UseQueryResult<Verblijfsobject[]> => {
  return useQuery({
    queryKey: ['verblijfsobjecten', bounds],
    queryFn: () => (bounds ? fetchBagVerblijfsobjecten(`${BAG_WFS_URL}&TYPENAME=bag:verblijfsobject`, bounds) : Promise.resolve([])),
    staleTime: 60 * 1000,
    enabled: !!bounds && enabled,
    placeholderData: previousData => previousData,
  });
};

export const useSendHandmatigeInvoer = (): UseMutationResult<Afzender, Error, Afzender> => {
  return useMutation<Afzender, Error, Afzender>({
    mutationFn: (newManualInput: Afzender) => {
      const registratiejaar = getRegistrationYear(newManualInput.relatie?.[0]?.registratiejaar);
      const endpoint = `${getManualInputBaseUrl(registratiejaar)}/input`;

      return fetchAuthenticatedForJson<Afzender>(endpoint, {
        json: registratiejaar >= 2025 ? normalizeV2EigendomstypeDeelnemer(newManualInput) : newManualInput,
        method: 'POST',
      });
    },
    mutationKey: [...QUERY_KEY],
  });
};

export const useValidate = (): UseMutationResult<Afzender, Error, Afzender> => {
  return useMutation<Afzender, Error, Afzender>({
    mutationFn: (newManualInput: Afzender) => {
      const registratiejaar = getRegistrationYear(newManualInput.relatie?.[0]?.registratiejaar);
      const endpoint = `${getManualInputBaseUrl(registratiejaar)}/input/validate`;

      return fetchAuthenticatedForJson<Afzender>(endpoint, {
        json: registratiejaar >= 2025 ? normalizeV2EigendomstypeDeelnemer(newManualInput) : newManualInput,
        method: 'PUT',
      });
    },
    mutationKey: [...QUERY_KEY],
  });
};
